import { useKeyboard } from '@react-native-community/hooks';
import { useTheme } from '@shopify/restyle';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Tag } from 'app/components/tag';
import { Column, Row } from 'app/design/primitives/box';
import { SearchInput } from 'app/design/primitives/input/search';
import { type FlashOrFlatList, ListPage } from 'app/design/primitives/page/list';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { Event, track } from 'app/lib/analytics';
import type { Group } from 'app/types/group';
import { useAllGroups } from 'app/network/groups';
import type { FoundUser } from 'app/network/user';
import { useFindUsers } from 'app/network/user';
import type { FiltersProps } from 'app/screens/groups/filters';
import { Filters } from 'app/screens/groups/filters';
import MemberCard from 'app/screens/groups/member-card';
import type { NavigationScreen } from 'app/types/navigation';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Dimensions, Keyboard, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import type { Address } from 'app/types/address';
import { useAllAddresses } from 'app/network/address';
import { navigateToUserProfile } from '../billboard/navigator-helper';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { ListingCardSkeleton } from 'app/components/listing-card-skeleton';
import type { Theme } from 'app/shopify';
import { IconSearch } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const WIDTH = Dimensions.get('window').width;
const PAGE_WIDTH = WIDTH * 0.9;
const GroupMembersScreenComponent: NavigationScreen<'group-members'> = ({ navigation, route }) => {
  const theme = useTheme<Theme>();

  const setPageTitle = usePageTitle('group-members');
  useEffect(() => setPageTitle('Members'), [setPageTitle]);

  const list = useRef<FlashOrFlatList<FoundUser>>(null as unknown as FlashOrFlatList<FoundUser>);
  const { data: groups, isPending: isGroupsPending } = useAllGroups();
  const { data: addresses, isPending: isAddressesPending } = useAllAddresses();

  const params = route.params;

  const groupIdsFromParams = isFeatureEnabled(FeatureFlag.justOneClub) ? groups?.map((g) => g.id) : params.groupIds;

  const groupsState = useState<Group['id'][] | undefined>(groupIdsFromParams ?? []);
  const [groupIds, setGroupIds] = groupsState;

  const addressesState = useState<Address['id'][] | undefined>(params.addressIds ?? []);
  const [addressIds, setAddressIds] = addressesState;

  const [search, setSearch] = useState<string>(params.search ?? '');
  const keyboard = useKeyboard();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isRefetching, isLoading, isPending, resetThenRefetch } =
    useFindUsers(
      {
        ...params,
        addressIds,
        groupIds,
        search: search,
      },
      { throwOnError: true, enabled: !!groupIds },
    );

  const onEndReached = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const closeTag = (id: string, isAddress: boolean) => {
    let resetFilters = false;
    if (isAddress) {
      if (addressIds?.length === 1 && groupIds?.length === 0) {
        resetFilters = true;
      }
      setAddressIds(addressIds?.filter((aId) => aId !== id));
    } else {
      if (groupIds?.length === 1 && addressIds?.length === 0) {
        resetFilters = true;
      }
      setGroupIds(groupIds?.filter((gId) => gId !== id));
    }
    if (resetFilters) {
      setAddressIds(addresses?.map((a) => a.id));
      setGroupIds(groups?.map((g) => g.id));
    }
  };

  useEffect(() => {
    track(Event.SearchMembers, {
      addressIds,
      groupIds,
      search: search,
    });
  }, [addressIds, groupIds, search]);

  const isRefreshing = isRefetching && !keyboard.keyboardShown;

  useRegisterLoading(isPending, 'GroupMembersScreen - fetching members');
  useRegisterLoading(isGroupsPending, 'GroupMembersScreen - fetching groups');
  useRegisterLoading(isAddressesPending, 'GroupMembersScreen - fetching addresses');

  return (
    <ListPage
      ItemSeparatorComponent={() => <Column marginTop="spacing.general.x4" />}
      ListEmptyComponent={
        isLoading ? (
          <ActivityIndicator />
        ) : !data ? (
          <Column gap="spacing.general.x4">
            <ListingCardSkeleton />
            <ListingCardSkeleton />
            <ListingCardSkeleton />
            <ListingCardSkeleton />
            <ListingCardSkeleton />
          </Column>
        ) : null
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Column marginTop="spacing.general.x8">
            <ActivityIndicator />
          </Column>
        ) : null
      }
      ListHeaderComponent={
        <ListHeaderComponent
          {...{
            target: undefined,
            setSearch,
            search: search,
            groups,
            groupsState,
            addresses,
            addressesState,
            closeTag,
          }}
        />
      }
      ListHeaderComponentStyle={{
        paddingTop: theme.spacing['spacing.general.x2'],
        paddingBottom: theme.spacing['spacing.general.x2'],
      }}
      data={data?.pages.map((page) => page.results).flat() ?? []}
      innerRef={list}
      keyExtractor={(item, index) => `${index.toString()}-${item.id}`}
      onEndReached={() => void onEndReached()}
      onEndReachedThreshold={3}
      onRefresh={resetThenRefetch}
      onScroll={Keyboard.dismiss}
      refreshing={isRefreshing}
      renderItem={({ item }) => (
        <Pressable onPress={() => navigateToUserProfile(navigation, item.id)}>
          <MemberCard {...item} showGroups={(groupIds?.length ?? 0) === 0} />
        </Pressable>
      )}
      showsVerticalScrollIndicator={false}
    />
  );
};

export const GroupMembersScreen: NavigationScreen<'group-members'> = withOptionalMetrics(
  'GroupMembersScreen',
  withErrorBoundary(GroupMembersScreenComponent),
);

type ListHeaderComponentProps = FiltersProps & {
  target: string | undefined;
  setSearch: (value: string) => void;
  search: string;
  closeTag: (groupId: string, isAddress: boolean) => void;
};

const ListHeaderComponent = ({
  target,
  setSearch,
  search,
  groups,
  groupsState,
  addresses,
  addressesState,
  closeTag,
}: ListHeaderComponentProps) => {
  const { isSmallUI, isLargeUI } = useUISize();

  const buildTagText = (groupId: string, groupsArray: (Group | Address)[]) => {
    const group = groupsArray.find((g) => g.id === groupId);
    return t('clubName', {
      ns: 'my-neighbourhood',
      clubName: group?.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }),
    });
  };

  const [selectedAddresses] = addressesState;
  const [selectedGroups] = groupsState;

  // Filtering per club is not really relevant; disabling this for now
  const showFilter = false;
  const showTags = false;
  // const showFilter = !isFeatureEnabled(FeatureFlag.justOneClub) && (groups?.length ?? 0) + (addresses?.length ?? 0) > 1;
  //
  // const showTags =
  //   showFilter &&
  //   ((selectedGroups && selectedGroups.length < (groups?.length ?? 0)) ||
  //     (selectedAddresses && selectedAddresses.length < (addresses?.length ?? 0)));

  const { t } = useScopedTranslation();
  const theme = useTheme<Theme>();
  return (
    <Column
      backgroundColor={target === 'StickyHeader' ? 'surface.default' : undefined}
      gap="spacing.general.x2"
      marginTop="spacing.general.x2"
    >
      {isSmallUI ? (
        <Text fontWeight="600" textAlign="center" variant="headerXSmall">
          {t('Members')}
        </Text>
      ) : null}
      <Row flex={1} gap="spacing.general.x2" marginBottom="spacing.general.x2" marginTop="spacing.general.x2">
        <Column flex={1}>
          <SearchInput
            initialValue={search}
            onSubmit={setSearch}
            {...(isLargeUI
              ? {
                  left: <IconSearch strokeWidth={ICON_STROKE_WIDTH} />,
                  right: <View style={{ height: 0, width: 0 }} />,
                }
              : {})}
          />
        </Column>

        {}
        {showFilter && (
          <Column width={52}>
            <Row
              backgroundColor="surface.inverse.tertiary-neutral"
              borderRadius="radius.sm"
              flex={1}
              paddingHorizontal="spacing.general.x2"
            >
              <Filters
                addresses={addresses}
                addressesState={addressesState}
                groups={groups}
                groupsState={groupsState}
              />
            </Row>
          </Column>
        )}
      </Row>

      {}
      {showTags && (
        <ScrollView
          automaticallyAdjustContentInsets={false}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            maxWidth: PAGE_WIDTH,
            paddingBottom: target === 'StickyHeader' ? theme.spacing['spacing.general.x2'] : undefined,
          }}
        >
          {selectedAddresses?.map((addressId) => (
            <Column key={addressId} marginRight="spacing.general.x2">
              <Tag onClose={() => closeTag(addressId, true)} text={buildTagText(addressId, addresses!)} />
            </Column>
          ))}
          {selectedGroups?.map((groupId) => (
            <Column key={groupId} marginRight="spacing.general.x2">
              <Tag onClose={() => closeTag(groupId, false)} text={buildTagText(groupId, groups!)} />
            </Column>
          ))}
        </ScrollView>
      )}
    </Column>
  );
};
